import { Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MAX_IMAGE_EDGE, MediaRejectedError, uploadExperienceImage } from '@/data/storageUpload';

interface GalleryImagesFieldProps {
  value: string[];
  onChange: (urls: string[]) => void;
}

/** Multi-image upload control, backed by Firebase Storage. */
export function GalleryImagesField({ value, onChange }: GalleryImagesFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFiles(files: File[]) {
    setUploading(true);
    setError(null);
    // Settled rather than `Promise.all`: one file the browser can't decode (a
    // HEIC off an iPhone is the likely one) would otherwise throw away the eight
    // that uploaded perfectly well alongside it, and the editor would have to
    // pick them all again to find out which one was the problem.
    const results = await Promise.allSettled(files.map(uploadExperienceImage));

    const urls = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
    if (urls.length > 0) onChange([...value, ...urls]);

    const failure = results.find((r) => r.status === 'rejected')?.reason;
    if (failure) {
      setError(failure instanceof MediaRejectedError ? failure.message : 'Some images failed to upload.');
    }
    setUploading(false);
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div>
      <div className='tw:flex tw:flex-wrap tw:gap-3'>
        {value.map((url, i) => (
          <div key={url} className='tw:relative'>
            <img src={url} alt='' className='tw:h-24 tw:w-24 tw:rounded-md tw:border tw:object-cover' />
            <button
              type='button'
              onClick={() => removeAt(i)}
              className='tw:absolute tw:-top-2 tw:-right-2 tw:rounded-full tw:bg-destructive tw:p-1 tw:text-destructive-foreground'
            >
              <X className='tw:size-3.5' />
            </button>
          </div>
        ))}

        <Button
          type='button'
          variant='outline'
          className='tw:h-24 tw:w-24 tw:flex-col'
          onClick={() => inputRef.current?.click()}
          loading={uploading}
        >
          <Upload />
          <span className='tw:text-xs'>{uploading ? 'Uploading…' : 'Add Images'}</span>
        </Button>
      </div>

      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        multiple
        className='tw:hidden'
        onChange={(e) => {
          // Copied out of the live FileList before the input is reset, because
          // Chromium empties that same list object in place on `value = ''` —
          // a retained reference goes to length 0 and the upload silently
          // never starts. Firefox hands back a fresh list and hides the bug.
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) handleFiles(files);
        }}
      />

      <p className='tw:mt-1.5 tw:text-xs tw:text-muted-foreground'>
        Each image is resized to {MAX_IMAGE_EDGE}px on its longest edge and saved as WebP.
      </p>

      {error && <p className='tw:mt-1.5 tw:text-sm tw:text-destructive'>{error}</p>}
    </div>
  );
}
